import { FC } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import { ButtonPrimary } from '~/components/Buttons/ButtonPrimary'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectCurrentSearchQuery, selectSearchModalVisibility, toggleSearchModal } from '~/redux/slices/search'

import searchAnalytics from '~/analytics/search/searchAnalytics'

import ResponsiveImage from '../Image/ResponsiveImage'

import { Result, Type } from './types'

const mediaQuery = [
  { screen: '(max-width: 1023px)', width: '70px' },
  { screen: '(min-width: 1024px)', width: '110px' },
]
export interface CardProps {
  results: Result[]
  isModal?: boolean
}

export const SearchResCard: FC<CardProps> = ({ results, isModal }) => {
  const open = useAppSelector(selectSearchModalVisibility)
  const { t } = useTranslation('search')
  const router = useRouter()

  // Message when search has no result or data
  if (!router.query.search && !results.length) {
    return <h1 className="text-center p-5 text-light-fade">{t('searchResultAppear')}</h1>
  } else if (!results.length) {
    return <h1 className="text-center p-5 text-light-fade">{t('noResult')}</h1>
  }

  return (
    <div
      className={`p-2 mb-8  max-w-screen-lg overflow-hidden scrollbar-thin text-black ${
        open ? 'scrollbar-thumb-purple scrollbar-track-purple-300  overflow-y-scroll' : ''
      }`}
      style={isModal && open ? { height: '60vh' } : { height: '100' }}
    >
      <div className="grid grid-flow-row grid-cols-1 gap-4 ">
        {results.map(({ header, type, path, description, topic }, index) =>
          header ? (
            <Topic
              description={description}
              index={index}
              key={path}
              path={path}
              title={header}
              topic={topic}
              type={type}
            />
          ) : null,
        )}
      </div>
    </div>
  )
}

function Topic({
  description,
  index,
  title,
  topic,
  type,
  path,
}: {
  description: string | null
  index: number
  title: string
  topic: string
  type: Type
  path: string
}): React.ReactElement {
  const router = useRouter()
  const { t } = useTranslation('search')
  const dispatch = useAppDispatch()
  const currentSearchQuery = useAppSelector(selectCurrentSearchQuery)

  const handleImage = (imgPath: string): React.ReactElement | undefined => {
    return (
      <ResponsiveImage
        alt={imgPath}
        height={110}
        imageClassName="w-18 lg:w-28"
        imageWidths="70, 110"
        mediaQuery={mediaQuery}
        srcKey={`icons/search-${imgPath}.png`}
        width={110}
      />
    )
  }

  const onNavigate = (): void => {
    dispatch(toggleSearchModal(false))
    searchAnalytics.logSearchResultClick(index, title, currentSearchQuery)
    router.push(path)
  }

  return (
    <div
      className="p-2 md:pt-4 md:pb-2 bg-white rounded shadow-experimental cursor-pointer md:max-h-52 md:min-h-48"
      onClick={onNavigate}
    >
      <div>
        <div className="md:hidden grid justify-center mb-4 mt-2">
          <div className="px-3 py-1 rounded bg-palepink text-xxxs text-center ">{t(type)}</div>
        </div>
        <div className="grid grid-cols-12">
          <div className="col-span-12 place-self-center md:place-self-start md:col-span-2 mb-3 mx-auto md:mx-2 md:mb-0">
            {handleImage(topic)}
          </div>
          <div className="col-span-12 md:col-span-10 place-self-center md:place-self-start w-full">
            <div className="flex justify-between items-start px-1">
              <div className="text-purple font-bold grow text-center md:text-left my-auto">{title}</div>
              <div className="hidden md:block rounded px-3 py-1 bg-palepink text-xxs text-center">{t(type)}</div>
            </div>
            <div className="flex flex-col overflow-hidden">
              <div className="pt-2 px-1 flex flex-col">
                <p className="line-clamp-3">{description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end mb-2">
        <ButtonPrimary className="mt-4 bg-purple text-white hover:bg-purple-dark text-center h-8" size="small">
          {type === Type.BOOTCAMP ? t('watchVideo') : t('readMore')}
        </ButtonPrimary>
      </div>
    </div>
  )
}
